import { Helmet } from "react-helmet-async";
import { filter } from "lodash";
import { sentenceCase } from "change-case";
import { useEffect, useState } from "react";
// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  Button,
  Popover,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TableHead,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TablePagination,
} from "@mui/material";
// components

import Iconify from "../components/iconify";
import Scrollbar from "../components/scrollbar";

import { useNavigate } from "react-router-dom";
import PickemDialog from "src/components/pickem-admin/PickemDialog";
import { usePickems } from "src/hooks/api/usePickems";
import { useAuth } from "src/hooks/api/useAuth";
// ----------------------------------------------------------------------

export default function GamePage() {
  const navigate = useNavigate();
  const { 
    loading, 
    fetchPickems, 
    deletePickem, 
    duplicatePickem, 
    createPickem 
  } = usePickems();
  const { getSSORedirectUrl } = useAuth();
  
  const [open, setOpen] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openCreatePickemDialog, setOpenCreatePickemDialog] = useState(false);
  const [rows, setRows] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const loadPickems = async () => {
      try {
        const pickems = await fetchPickems(page * rowsPerPage, rowsPerPage);
        setRows(pickems);
      } catch (error) {
        console.error("Failed to load pickems:", error);
      }
    };
    loadPickems();
  }, [page, rowsPerPage, fetchPickems]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleOpenMenu = (event) => {
    setOpen(event.currentTarget);
    setOpenId(event.currentTarget.id);
  };

  const handleCloseMenu = () => {
    setOpen(null);
    setOpenId(null);
  };

  const deleteGameHandler = async () => {
    setOpen(false);
    setOpenConfirm(false);
    try {
      await deletePickem(openId);
      window.location.reload(false);
    } catch (error) {
      console.error("Failed to delete pickem:", error);
    }
  };
  
  const goToAppHandler = async () => {
    try {
      const redirectUrl = await getSSORedirectUrl();
      window.open(redirectUrl, "_blank");
      window.location.reload(false);
    } catch (error) {
      console.error("Failed to get SSO redirect URL:", error);
    }
  };
  
  const duplicatePickemHandler = async () => {
    setOpen(false);
    try {
      await duplicatePickem(openId);
      window.location.reload(false);
    } catch (error) {
      console.error("Failed to duplicate pickem:", error);
    }
  };

  // Create Pickem
  const createPickemHandler = async (pickem) => {
    setOpenCreatePickemDialog(false);
    try {
      await createPickem(pickem);
      window.location.reload(false);
    } catch (error) {
      console.error("Failed to create pickem:", error);
    }
  };

  const closeCreatePickemDialog = () => {
    setOpenCreatePickemDialog(false);
  };

  return (
    <>
      <Helmet>
        <title> Pickems </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Pickems
          </Typography>
          <Box>
            <Button
              variant="standard"
              startIcon={<Iconify icon="eva:paper-plane-outline" />}
              onClick={() => goToAppHandler()}
              sx={{ mr: 2 }}
            >
              Go To PrePlay
            </Button>
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={() => setOpenCreatePickemDialog(true)}
            >
              New Pickem
            </Button>
          </Box>
        </Stack>

        <Card>
          {rows === null || loading ? (
            <Box
              sx={{ display: "flex", justifyContent: "center" }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Scrollbar>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Start</TableCell>
                      <TableCell align="right"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell component="th" scope="row">
                          {row.title_en}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {row.status}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {row.start}
                        </TableCell>

                        <TableCell align="right">
                          <IconButton
                            size="large"
                            color="inherit"
                            onClick={handleOpenMenu}
                            id={row.pickem_id}
                          >
                            <Iconify icon={"eva:more-vertical-fill"} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          )}
        </Card>
      </Container>
      <TablePagination
        sx={{ marginRight: "20px" }}
        rowsPerPageOptions={[10, 20]}
        component="div"
        count={-1}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            "& .MuiMenuItem-root": {
              px: 1,
              typography: "body2",
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem
          onClick={() =>
            navigate(`/dashboard/leaderboards/view?pickem_id=${openId}`)
          }
        >
          <Iconify icon={"eva:list-outline"} sx={{ mr: 1 }} />
          Leaderboard
        </MenuItem>
        <MenuItem onClick={() => duplicatePickemHandler()}>
          <Iconify icon={"eva:copy-outline"} sx={{ mr: 1 }} />
          Duplicate
        </MenuItem>
        <MenuItem
          onClick={() => navigate(`/dashboard/pickem/edit?pickem_id=${openId}`)}
        >
          <Iconify icon={"eva:edit-outline"} sx={{ mr: 1 }} />
          Edit
        </MenuItem>
      </Popover>
      <Dialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Are you sure you want to delete Game?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            All questions and cards would be deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirm(false)}>Cancel</Button>
          <Button onClick={() => deleteGameHandler(true)} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <PickemDialog
        open={openCreatePickemDialog}
        onSuccessHandler={createPickemHandler}
        onClose={closeCreatePickemDialog}
      />
    </>
  );
}
